import { OptionalEnumSchema, isDefined } from 'notable-common';
import { z } from 'zod';
import { FlowRpaJobKind, FlowRpaJobSuccessFilter } from '../../../rpaJob/rpaJob.js';
import { FlowBlockValueTemplateDurationSchema } from '../../blockValue/templateDuration.js';
import { BaseFlowBlockSchema } from '../block.js';
import { FlowConsumerOfPatientSchema } from '../consumer.js';
import { FlowFilterConfigSchema } from '../filterConfig.js';
import { BaseFlowNotableObjectConfigSchema, FlowNotableObject } from '../notableObject.js';
import { FlowBlockType } from '../type.js';
export const FLOW_MAX_RPA_JOB_CONTEXT_LIMIT = 25;
export var FlowGetNotableObjectProviderIdType;
(function (FlowGetNotableObjectProviderIdType) {
    FlowGetNotableObjectProviderIdType["USER_ID"] = "USER_ID";
    FlowGetNotableObjectProviderIdType["EHR_PROVIDER_INFORMATION_ID"] = "EHR_PROVIDER_INFORMATION_ID";
    FlowGetNotableObjectProviderIdType["NPI"] = "NPI";
})(FlowGetNotableObjectProviderIdType || (FlowGetNotableObjectProviderIdType = {}));
export var FlowFeedMessageFormat;
(function (FlowFeedMessageFormat) {
    FlowFeedMessageFormat["X12"] = "X12";
    FlowFeedMessageFormat["HL7"] = "HL7";
    FlowFeedMessageFormat["JSON"] = "JSON";
})(FlowFeedMessageFormat || (FlowFeedMessageFormat = {}));
export var FlowPatientUploadDocumentType;
(function (FlowPatientUploadDocumentType) {
    FlowPatientUploadDocumentType["DocumentSigner"] = "DOCUMENT_SIGNER";
    FlowPatientUploadDocumentType["FormSummary"] = "FORM_SUMMARY";
    FlowPatientUploadDocumentType["FreehandBodyDiagram"] = "FREEHAND_BODY_DIAGRAM";
    FlowPatientUploadDocumentType["InsuranceCard"] = "INSURANCE_CARD";
    FlowPatientUploadDocumentType["Image"] = "IMAGE";
    FlowPatientUploadDocumentType["OcrVerification"] = "OCR_VERIFICATION";
    FlowPatientUploadDocumentType["PhotoID"] = "PHOTO_ID";
})(FlowPatientUploadDocumentType || (FlowPatientUploadDocumentType = {}));
export var FlowPatientMatchStrictness;
(function (FlowPatientMatchStrictness) {
    FlowPatientMatchStrictness["Strict"] = "Strict";
    FlowPatientMatchStrictness["TwoOrMore"] = "TwoOrMore";
    FlowPatientMatchStrictness["ThreeOrMore"] = "ThreeOrMore";
})(FlowPatientMatchStrictness || (FlowPatientMatchStrictness = {}));
export const FlowGetPaymentConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Payment),
});
export const FlowGetReferenceTableConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.ReferenceTable),
    referenceTableId: z.uuid(),
    selectedColumns: z.array(z.string()).optional(),
    filterConfig: FlowFilterConfigSchema.optional(),
});
export const FlowGetHl7MessageEventConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Hl7MessageEvent),
});
export const FlowGetAgentConversationConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.AgentConversation),
});
export const FlowGetGuarantorConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Guarantor),
    patient: FlowConsumerOfPatientSchema({ isRequired: true }),
});
export var FlowGetOrderOption;
(function (FlowGetOrderOption) {
    FlowGetOrderOption["GetOneById"] = "GetOneById";
    FlowGetOrderOption["FindAll"] = "FindAll";
})(FlowGetOrderOption || (FlowGetOrderOption = {}));
export var FlowGetExternalDocumentPacketOption;
(function (FlowGetExternalDocumentPacketOption) {
    FlowGetExternalDocumentPacketOption["GetOneById"] = "GetOneById";
    FlowGetExternalDocumentPacketOption["FindByFileName"] = "FindByFileName";
})(FlowGetExternalDocumentPacketOption || (FlowGetExternalDocumentPacketOption = {}));
export const FlowGetPatientOrderConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientOrder),
    patientOrderId: z.string().optional(),
    patient: FlowConsumerOfPatientSchema({ isRequired: false }),
    getOrderOption: OptionalEnumSchema(FlowGetOrderOption, FlowGetOrderOption.GetOneById),
});
export const FlowGetProviderConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Provider),
    providerIdType: z.enum(FlowGetNotableObjectProviderIdType),
    isSelectMultipleProviderIdsEnabled: z.boolean().default(false),
    // `providerId` should really not be optional, but currently it _can_ end up as undefined with the multiple-provider input.
    // It can also end up actually being an array somehow when `isSelectMultipleProviderIdsEnabled=true`. I don't know. It's a mess.
    providerId: z.string().optional(),
});
const FlowGetExternalDocumentPacketBaseSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.ExternalDocumentPacket),
});
const FlowGetExternalDocumentPacketByIdSchema = FlowGetExternalDocumentPacketBaseSchema.extend({
    getExternalDocumentPacketOption: z.literal(FlowGetExternalDocumentPacketOption.GetOneById),
    externalDocumentPacketId: z.string().min(1),
});
const FlowGetExternalDocumentPacketByFileNameSchema = FlowGetExternalDocumentPacketBaseSchema.extend({
    getExternalDocumentPacketOption: z.literal(FlowGetExternalDocumentPacketOption.FindByFileName),
    fileName: z.string().min(1),
});
export const FlowGetExternalDocumentPacketConfigSchema = FlowGetExternalDocumentPacketBaseSchema.extend({
    getExternalDocumentPacketOption: z
        .enum(FlowGetExternalDocumentPacketOption)
        .default(FlowGetExternalDocumentPacketOption.GetOneById),
    externalDocumentPacketId: z.string().optional(),
    fileName: z.string().optional(),
});
function withDefaultDocumentPacketOption(value) {
    if (isDefined(value.getExternalDocumentPacketOption)) {
        return value;
    }
    return {
        ...value,
        getExternalDocumentPacketOption: FlowGetExternalDocumentPacketOption.GetOneById,
    };
}
export const FlowStrictExternalDocumentPacketConfigSchema = z.preprocess((value) => withDefaultDocumentPacketOption(value), z.discriminatedUnion('getExternalDocumentPacketOption', [
    FlowGetExternalDocumentPacketByIdSchema,
    FlowGetExternalDocumentPacketByFileNameSchema,
]));
export const FlowGetTemplateConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Template),
    ehrInformationId: z.string(),
    templateId: z.string(),
});
export const FlowGetPatientConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Patient),
    ehrInformationId: z.string().optional(),
    lastName: z.string().optional(),
    firstName: z.string().optional(),
    mrn: z.string().optional(),
    phoneNumber: z.string().optional(),
    dateOfBirth: z.string().optional(),
    patientMatchStrictness: z
        .enum(FlowPatientMatchStrictness)
        .optional()
        .default(FlowPatientMatchStrictness.Strict),
});
export const FlowGetPracticeConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Practice),
    isSelectPracticeIdsEnabled: z.boolean().default(false),
    practiceIds: z.string().optional(),
});
export const FlowGetSftpMessageEventConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.SftpMessageEvent),
});
export const FlowGetX12MessageEventConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.X12MessageEvent),
});
export const FlowGetPatientUploadConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientUpload),
    documentType: z.enum(FlowPatientUploadDocumentType).optional(),
    patientInsuranceInformationId: z.string().optional(),
    previsitFormTemplateId: z.string().optional(),
});
export const FlowGetPatientInsuranceInformationConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PatientInsuranceInformation),
});
export var FlowGetEncounterMode;
(function (FlowGetEncounterMode) {
    FlowGetEncounterMode["GetOneById"] = "GetOneById";
    FlowGetEncounterMode["FindAll"] = "FindAll";
})(FlowGetEncounterMode || (FlowGetEncounterMode = {}));
export const FlowGetEncountersConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Encounter),
    mode: z.enum(FlowGetEncounterMode).default(FlowGetEncounterMode.FindAll),
    patient: FlowConsumerOfPatientSchema({ isRequired: false }),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    encounterId: z.string().optional(),
});
export const FlowGetPrimaryCareProviderConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.PrimaryCareProvider),
});
export const FlowGetFeedMessagesConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.FeedMessages),
    source: z.string().optional(),
    limit: z.string().optional(),
    messageFormat: z.enum(FlowFeedMessageFormat).optional(),
});
export const FlowGetEncounterContactsConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.EncounterContacts),
    encounterId: z.string(),
});
export const FlowGetInsuranceOcrResultsConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.InsuranceOcrResults),
    patientUploadId: z.string().min(1, { message: 'Patient upload ID is required' }),
});
export var FlowGetJobContextMode;
(function (FlowGetJobContextMode) {
    FlowGetJobContextMode["GetOneById"] = "GetOneById";
    FlowGetJobContextMode["FindAll"] = "FindAll";
})(FlowGetJobContextMode || (FlowGetJobContextMode = {}));
export const FlowGetJobContextConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.JobContext),
    mode: z.enum(FlowGetJobContextMode).default(FlowGetJobContextMode.FindAll),
    rpaJobId: z.string().optional(),
    jobKind: z.enum(FlowRpaJobKind).optional(),
    successFilter: z.enum(FlowRpaJobSuccessFilter).optional(),
    lookbackDuration: FlowBlockValueTemplateDurationSchema().optional(),
    limit: z.string().optional(),
});
export const FlowGetAssistantConfigSchema = BaseFlowNotableObjectConfigSchema.extend({
    notableObject: z.literal(FlowNotableObject.Assistant),
    assistantId: z.string(),
});
export const FlowGetNotableObjectConfigSchema = z.discriminatedUnion('notableObject', [
    FlowGetReferenceTableConfigSchema,
    FlowGetPaymentConfigSchema,
    FlowGetHl7MessageEventConfigSchema,
    FlowGetAgentConversationConfigSchema,
    FlowGetGuarantorConfigSchema,
    FlowGetPatientOrderConfigSchema,
    FlowGetTemplateConfigSchema,
    FlowGetProviderConfigSchema,
    FlowGetExternalDocumentPacketConfigSchema,
    FlowGetPatientConfigSchema,
    FlowGetPracticeConfigSchema,
    FlowGetSftpMessageEventConfigSchema,
    FlowGetX12MessageEventConfigSchema,
    FlowGetPatientUploadConfigSchema,
    FlowGetPatientInsuranceInformationConfigSchema,
    FlowGetEncountersConfigSchema,
    FlowGetPrimaryCareProviderConfigSchema,
    FlowGetFeedMessagesConfigSchema,
    FlowGetEncounterContactsConfigSchema,
    FlowGetInsuranceOcrResultsConfigSchema,
    FlowGetJobContextConfigSchema,
    FlowGetAssistantConfigSchema,
]);
export const FlowGetNotableObjectBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.GetNotableObject),
    config: FlowGetNotableObjectConfigSchema,
});
//# sourceMappingURL=config.js.map